import { call, put, takeLatest, select } from 'redux-saga/effects';
import { message } from 'antd';
import * as types from '../constants/actionTypes';
import * as actions from '../actions/billReceiptActions';
import { billAPI } from '../../api/config';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getCurrentFilters = (state) => state.billReceipts.filters;

function* refreshListAndStats(filters) {
  yield put(actions.fetchBillsRequest({ page: filters.page || 1, limit: filters.limit || 10 }));
  yield put(actions.fetchBillStatisticsRequest());
}

// ─── Workers ──────────────────────────────────────────────────────────────────

/**
 * Fetch paginated list of bill receipts.
 * GET /api/bills
 */
function* fetchBillsSaga(action) {
  try {
    const response = yield call(billAPI.listBills, action.payload);
    yield put(actions.fetchBillsSuccess(response.data));
  } catch (error) {
    const msg = error.response?.data?.error || 'Failed to fetch bill receipts';
    yield put(actions.fetchBillsFailure(msg));
    message.error(msg);
  }
}

/**
 * Fetch a single bill receipt by ID.
 * GET /api/bills/:id
 */
function* fetchBillSaga(action) {
  try {
    const response = yield call(billAPI.getBillById, action.payload);
    yield put(actions.fetchBillSuccess(response.data));
  } catch (error) {
    const msg = error.response?.data?.error || 'Failed to fetch bill receipt';
    yield put(actions.fetchBillFailure(msg));
    message.error(msg);
  }
}

/**
 * Record (receive) a new bill receipt.
 * POST /api/bills/receive
 */
function* receiveBillSaga(action) {
  try {
    const response = yield call(billAPI.receiveBill, action.payload);
    yield put(actions.receiveBillSuccess(response.data));
    message.success(response.data?.message || 'Bill receipt recorded successfully');
    const filters = yield select(getCurrentFilters);
    yield put(actions.fetchPendingBillChallansRequest({ page: 1, limit: 10 }));
    yield* refreshListAndStats(filters);
  } catch (error) {
    const msg =
      error.response?.data?.details ||
      error.response?.data?.error ||
      'Failed to record bill receipt';
    yield put(actions.receiveBillFailure(msg));
    message.error(msg);
  }
}

/**
 * Verify a bill receipt: received → verified.
 * PATCH /api/bills/:id/verify
 */
function* verifyBillSaga(action) {
  try {
    const { id, data } = action.payload;
    const response = yield call(billAPI.verifyBill, id, data);
    yield put(actions.verifyBillSuccess(response.data));
    message.success(response.data?.message || 'Bill verified successfully');
    const filters = yield select(getCurrentFilters);
    yield* refreshListAndStats(filters);
  } catch (error) {
    const details =
      error.response?.data?.details ||
      error.response?.data?.error ||
      'Failed to verify bill receipt';

    // Enhanced error handling for strict verification
    const errorType = details.includes('BILL VERIFICATION BLOCKED')
      ? 'CRITICAL_MISMATCH'
      : details.includes('BILL TOTAL VALIDATION FAILED')
      ? 'TOTAL_MISMATCH'
      : 'UNKNOWN';

    yield put(actions.verifyBillFailure({
      message: error.response?.data?.error || 'Verification failed',
      details,
      type: errorType
    }));

    // Show brief error in toast, full details will be in the modal/detail view
    if (errorType === 'CRITICAL_MISMATCH') {
      message.error('Verification blocked due to critical mismatches. Please review the details.', 6);
    } else if (errorType === 'TOTAL_MISMATCH') {
      message.error('Bill totals do not match item calculations. Please correct the data.', 6);
    } else {
      message.error(details, 5);
    }
  }
}

/**
 * Reject a bill receipt: received|verified → rejected.
 * PATCH /api/bills/:id/reject
 */
function* rejectBillSaga(action) {
  try {
    const { id, data } = action.payload;
    const response = yield call(billAPI.rejectBill, id, data);
    yield put(actions.rejectBillSuccess(response.data));
    message.success(response.data?.message || 'Bill rejected. Linked challans reverted to pending.');
    const filters = yield select(getCurrentFilters);
    yield put(actions.fetchPendingBillChallansRequest({ page: 1, limit: 10 }));
    yield* refreshListAndStats(filters);
  } catch (error) {
    const msg =
      error.response?.data?.details ||
      error.response?.data?.error ||
      'Failed to reject bill receipt';
    yield put(actions.rejectBillFailure(msg));
    message.error(msg);
  }
}

/**
 * Close a bill receipt: verified → closed.
 * PATCH /api/bills/:id/close
 */
function* closeBillSaga(action) {
  try {
    const { id, data } = action.payload;
    const response = yield call(billAPI.closeBill, id, data);
    yield put(actions.closeBillSuccess(response.data));
    message.success(response.data?.message || 'Bill closed successfully');
    const filters = yield select(getCurrentFilters);
    yield* refreshListAndStats(filters);
  } catch (error) {
    const msg =
      error.response?.data?.details ||
      error.response?.data?.error ||
      'Failed to close bill receipt';
    yield put(actions.closeBillFailure(msg));
    message.error(msg);
  }
}

/**
 * Fetch challans with no bill yet (bill_status = pending).
 * GET /api/bills/pending-challans
 */
function* fetchPendingBillChallansSaga(action) {
  try {
    const response = yield call(billAPI.getPendingChallans, action.payload);
    yield put(actions.fetchPendingBillChallansSuccess(response.data));
  } catch (error) {
    const msg =
      error.response?.data?.error || 'Failed to fetch pending bill challans';
    yield put(actions.fetchPendingBillChallansFailure(msg));
    message.error(msg);
  }
}

/**
 * Fetch bill summary for a single PO.
 * GET /api/purchase-orders/:id/bill-summary
 */
function* fetchPOBillSummarySaga(action) {
  try {
    const response = yield call(billAPI.getPOBillSummary, action.payload);
    yield put(actions.fetchPOBillSummarySuccess(response.data));
  } catch (error) {
    const msg =
      error.response?.data?.error || 'Failed to fetch PO bill summary';
    yield put(actions.fetchPOBillSummaryFailure(msg));
    message.error(msg);
  }
}

/**
 * Fetch 3-way reconciliation report for a single PO.
 * GET /api/purchase-orders/:id/reconciliation
 */
function* fetchPOReconciliationSaga(action) {
  try {
    const response = yield call(billAPI.getPOReconciliation, action.payload);
    yield put(actions.fetchPOReconciliationSuccess(response.data));
  } catch (error) {
    const msg =
      error.response?.data?.error || 'Failed to fetch PO reconciliation report';
    yield put(actions.fetchPOReconciliationFailure(msg));
    message.error(msg);
  }
}

/**
 * Fetch aggregated bill statistics.
 * GET /api/bills/statistics
 */
function* fetchBillStatisticsSaga() {
  try {
    const response = yield call(billAPI.getStatistics);
    yield put(actions.fetchBillStatisticsSuccess(response.data));
  } catch (error) {
    const msg =
      error.response?.data?.error || 'Failed to fetch bill statistics';
    yield put(actions.fetchBillStatisticsFailure(msg));
    message.error(msg);
  }
}

// ─── Watchers ─────────────────────────────────────────────────────────────────

export default function* billReceiptSaga() {
  yield takeLatest(types.FETCH_BILLS_REQUEST, fetchBillsSaga);
  yield takeLatest(types.FETCH_BILL_REQUEST, fetchBillSaga);
  yield takeLatest(types.RECEIVE_BILL_REQUEST, receiveBillSaga);
  yield takeLatest(types.VERIFY_BILL_REQUEST, verifyBillSaga);
  yield takeLatest(types.REJECT_BILL_REQUEST, rejectBillSaga);
  yield takeLatest(types.CLOSE_BILL_REQUEST, closeBillSaga);
  yield takeLatest(types.FETCH_PENDING_BILL_CHALLANS_REQUEST, fetchPendingBillChallansSaga);
  yield takeLatest(types.FETCH_PO_BILL_SUMMARY_REQUEST, fetchPOBillSummarySaga);
  yield takeLatest(types.FETCH_PO_RECONCILIATION_REQUEST, fetchPOReconciliationSaga);
  yield takeLatest(types.FETCH_BILL_STATISTICS_REQUEST, fetchBillStatisticsSaga);
}
